import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {api} from '../../api';
import Layout from '../components/Layout';

export default function ReceiptDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    api(`/receipts/${id}`).then(r => r.json()).then(data => { setReceipt(data); setForm(data); });
    api(`/receipts/${id}/image-url`).then(r => r.json()).then(data => setImageUrl(data.url));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    const body = {
      vendor: form.vendor,
      receiptDate: form.receiptDate,
      subtotal: form.subtotal,
      tax: form.tax,
      total: form.total,
      currency: form.currency,
      paymentMethod: form.paymentMethod,
      status: form.status,
      lineItems: form.lineItems,
    };
    const res = await api(`/receipts/${id}`, { method: 'PUT', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body) });
    if (res.ok) {
      const updated = await res.json();
      setReceipt(updated);
      setForm(updated);
      setEditing(false);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    await api(`/receipts/${id}`, { method: 'DELETE' });
    navigate('/receipts');
  };

  const handleMarkReviewed = async () => {
    const res = await api(`/receipts/${id}`, { method: 'PUT', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({ status: 'REVIEWED' }) });
    if (res.ok) { const updated = await res.json(); setReceipt(updated); setForm(updated); }
  };

  if (!receipt) return <Layout><p className="text-gray-500">Loading...</p></Layout>;

  return (
    <Layout>
      <div className="mb-4">
        <button onClick={() => navigate('/receipts')} className="text-gray-600 hover:text-gray-900">&larr; Back to Receipts</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Image */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-3">Receipt Image</h3>
          {imageUrl ? (
            <img src={imageUrl} alt="Receipt" className="max-w-full rounded border" />
          ) : <p className="text-gray-400">Loading image...</p>}
        </div>

        {/* Extracted Data */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Extracted Data</h3>
            <div className="flex gap-2">
              {!editing && receipt.status === 'EXTRACTED' && (
                <button onClick={handleMarkReviewed} className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700">Mark Reviewed</button>
              )}
              {!editing ? (
                <button onClick={() => setEditing(true)} className="text-sm bg-lime-600 text-white px-3 py-1 rounded hover:bg-lime-700">Edit</button>
              ) : (
                <>
                  <button onClick={() => { setEditing(false); setForm(receipt); }} className="text-sm bg-gray-200 text-gray-700 px-3 py-1 rounded">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="text-sm bg-lime-600 text-white px-3 py-1 rounded hover:bg-lime-700 disabled:opacity-50">Save</button>
                </>
              )}
              <button onClick={() => setShowDeleteModal(true)} className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700">Delete</button>
            </div>
          </div>

          <div className="space-y-4">
            <Field label="Vendor" value={form.vendor} editing={editing} onChange={v => setForm({...form, vendor: v})} />
            <Field label="Date" value={form.receiptDate} editing={editing} type="date" onChange={v => setForm({...form, receiptDate: v})} />
            <Field label="Subtotal" value={form.subtotal} editing={editing} type="number" onChange={v => setForm({...form, subtotal: parseFloat(v) || 0})} />
            <Field label="Tax" value={form.tax} editing={editing} type="number" onChange={v => setForm({...form, tax: parseFloat(v) || 0})} />
            <Field label="Total" value={form.total} editing={editing} type="number" onChange={v => setForm({...form, total: parseFloat(v) || 0})} />
            <Field label="Currency" value={form.currency} editing={editing} onChange={v => setForm({...form, currency: v})} />
            <Field label="Payment Method" value={form.paymentMethod} editing={editing} onChange={v => setForm({...form, paymentMethod: v})} />
          </div>

          {/* Line Items */}
          {form.lineItems && form.lineItems.length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium text-gray-700 mb-2">Line Items</h4>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1">Description</th>
                    <th className="text-right py-1">Qty</th>
                    <th className="text-right py-1">Price</th>
                    <th className="text-right py-1">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {form.lineItems.map((item, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-1">{item.description || '—'}</td>
                      <td className="text-right py-1">{item.quantity || '—'}</td>
                      <td className="text-right py-1">{item.unitPrice != null ? `$${item.unitPrice.toFixed(2)}` : '—'}</td>
                      <td className="text-right py-1">{item.amount != null ? `$${item.amount.toFixed(2)}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Status */}
          <div className="mt-6 pt-4 border-t">
            <p className="text-sm text-gray-500">Status: <span className="font-medium">{receipt.status}</span></p>
            <p className="text-sm text-gray-500">File: {receipt.fileName}</p>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Receipt</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this receipt? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Field({ label, value, editing, type = 'text', onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600">{label}</label>
      {editing ? (
        <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
          className="mt-1 w-full border border-gray-300 rounded px-3 py-2 text-sm" step={type === 'number' ? '0.01' : undefined} />
      ) : (
        <p className="mt-1 text-gray-900">{type === 'number' && value != null ? `$${Number(value).toFixed(2)}` : value || '—'}</p>
      )}
    </div>
  );
}
